import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DECKS } from '../data/stimuli';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/categories.css';

const BIN_COLORS = ['#D6F5DC', '#F2E4D0', '#DCEAFB', '#F9E2E9', '#EAE2F8', '#FDEBD3'];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Builds the sorting activity live from whichever decks were picked on the
// setup screen — nothing is pre-made or saved, so this runs fresh every time.
function buildDeckFromNames(deckNames) {
  if (!deckNames || deckNames.length < 2) return null;
  const bins = deckNames.map((name, i) => {
    const source = DECKS.find(d => d.name === name);
    if (!source) return null;
    return {
      id:    `bin_${i}`,
      label: source.name,
      coverImage: source.coverImage,
      color: BIN_COLORS[i % BIN_COLORS.length],
      items: source.items.filter(c => c.image).map((c, j) => ({
        id: `item_${i}_${j}`, label: c.label, emoji: c.emoji, image: c.image, imageBackup: c.imageBackup || null,
      })),
    };
  }).filter(Boolean);
  if (bins.length < 2) return null;
  return { title: `Sort: ${deckNames.join(' vs ')}`, bins };
}

function makeInitialState(deck) {
  const bins = deck?.bins || [];
  const pool = shuffle(bins.flatMap(bin => bin.items.map(item => ({
    ...item, correctBinId: bin.id,
  }))));
  const binContents = Object.fromEntries(bins.map(b => [b.id, []]));
  return { pool, binContents };
}

// ── One card, used for both the pool and a placed bin item ──────────────────

function CatCard({ item, placed, wrong, dragHandlers }) {
  const { tc } = useTranslation();
  return (
    <div
      className={[
        'cat-card',
        placed ? 'cat-card--placed' : '',
        wrong  ? 'cat-card--wrong'  : '',
      ].filter(Boolean).join(' ')}
      {...dragHandlers}
    >
      <div className="cat-card-img-wrap">
        {item.image
          ? <img src={item.image} alt={tc(item.label)} className="cat-card-img" draggable={false} />
          : <div className="cat-card-emoji-bg"><span className="cat-card-emoji">{item.emoji}</span></div>
        }
        {placed && <div className="cat-card-badge cat-card-badge--correct">✓</div>}
      </div>
      <div className="cat-card-label">{tc(item.label)}</div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const CategoriesPage = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const { t, tc } = useTranslation();

  const deck = useMemo(() => buildDeckFromNames(location.state?.deckNames), [location.state]);
  const bins = useMemo(() => deck?.bins || [], [deck]);

  const [activity, setActivity] = useState(() => makeInitialState(deck));
  const [phase, setPhase]       = useState('play'); // 'play' | 'complete'
  const [dragOverBin, setDragOverBin] = useState(null);
  const [wrongItemId, setWrongItemId] = useState(null);
  const [mistakes,    setMistakes]    = useState(0);
  const [celebrating, setCelebrating] = useState(false);

  const { pool, binContents } = activity;
  const totalItems = bins.reduce((s, b) => s + b.items.length, 0);
  const sortedCount = totalItems - pool.length;

  const resetAll = () => {
    setActivity(makeInitialState(deck));
    setPhase('play');
    setMistakes(0);
    setCelebrating(false);
  };

  // Re-run the whole activity fresh whenever a different deck loads
  useEffect(() => { resetAll(); }, [deck?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const wrongTimeout = useRef(null);

  const handleDropOnBin = (poolIndex, binId) => {
    const item = pool[poolIndex];
    if (!item) return;

    if (item.correctBinId === binId) {
      setActivity(prev => {
        const newPool = [...prev.pool];
        newPool.splice(poolIndex, 1);
        const newBinContents = { ...prev.binContents, [binId]: [...prev.binContents[binId], item] };
        return { pool: newPool, binContents: newBinContents };
      });
      return;
    }

    setMistakes(m => m + 1);
    setWrongItemId(item.id);
    if (wrongTimeout.current) clearTimeout(wrongTimeout.current);
    wrongTimeout.current = setTimeout(() => setWrongItemId(null), 650);
  };

  useEffect(() => {
    if (totalItems > 0 && pool.length === 0 && phase === 'play') {
      setPhase('complete');
      setCelebrating(true);
      setTimeout(() => setCelebrating(false), 3600);
    }
  }, [pool.length, totalItems, phase]);

  useEffect(() => () => { if (wrongTimeout.current) clearTimeout(wrongTimeout.current); }, []);

  // ── Desktop HTML5 drag ──────────────────────────────────────────────────────

  const desktopSrcIndex = useRef(null);
  const desktopSrcEl     = useRef(null);

  const onDragStart = (e, index) => {
    desktopSrcIndex.current = index;
    desktopSrcEl.current    = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    requestAnimationFrame(() => {
      if (desktopSrcEl.current) desktopSrcEl.current.style.opacity = '0.3';
    });
  };

  const onDragOverBin = (e, binId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBin(binId);
  };

  const onDropOnBin = (e, binId) => {
    e.preventDefault();
    setDragOverBin(null);
    if (desktopSrcIndex.current != null) handleDropOnBin(desktopSrcIndex.current, binId);
    desktopSrcIndex.current = null;
    if (desktopSrcEl.current) { desktopSrcEl.current.style.opacity = ''; desktopSrcEl.current = null; }
  };

  const onDragEnd = () => {
    setDragOverBin(null);
    desktopSrcIndex.current = null;
    if (desktopSrcEl.current) { desktopSrcEl.current.style.opacity = ''; desktopSrcEl.current = null; }
  };

  // ── Touch drag ──────────────────────────────────────────────────────────────

  const ghost       = useRef(null);
  const ghostOffset = useRef({ x: 0, y: 0 });
  const touchSrcIndex = useRef(null);
  const touchTargetBin = useRef(null);
  const touchSrcEl    = useRef(null);

  const onTouchStart = (e, index) => {
    if (phase === 'complete') return;
    e.preventDefault();
    const touch = e.touches[0];
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();

    touchSrcIndex.current = index;
    touchSrcEl.current    = el;
    touchTargetBin.current = null;
    el.style.opacity = '0.2';

    ghostOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    const g = el.cloneNode(true);
    Object.assign(g.style, {
      position: 'fixed', left: rect.left + 'px', top: rect.top + 'px',
      width: rect.width + 'px', height: rect.height + 'px',
      pointerEvents: 'none', zIndex: '9999', opacity: '0.93',
      transform: 'scale(1.07) rotate(2deg)', boxShadow: '0 24px 56px rgba(0,0,0,0.32)',
      borderRadius: '16px', transition: 'none',
    });
    document.body.appendChild(g);
    ghost.current = g;

    const onMove = (ev) => {
      ev.preventDefault();
      const t = ev.touches[0];
      if (ghost.current) {
        ghost.current.style.left = (t.clientX - ghostOffset.current.x) + 'px';
        ghost.current.style.top  = (t.clientY - ghostOffset.current.y) + 'px';
      }
      const under = document.elementFromPoint(t.clientX, t.clientY);
      const binEl = under?.closest('[data-bin]');
      if (binEl) {
        touchTargetBin.current = binEl.dataset.bin;
        setDragOverBin(binEl.dataset.bin);
      } else {
        touchTargetBin.current = null;
        setDragOverBin(null);
      }
    };

    const onEnd = () => {
      if (ghost.current)      { ghost.current.remove();      ghost.current = null; }
      if (touchSrcEl.current) { touchSrcEl.current.style.opacity = ''; touchSrcEl.current = null; }
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      if (touchTargetBin.current != null && touchSrcIndex.current != null) {
        handleDropOnBin(touchSrcIndex.current, touchTargetBin.current);
      }
      touchSrcIndex.current  = null;
      touchTargetBin.current = null;
      setDragOverBin(null);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { once: true });
  };

  // ── Nothing picked yet (direct nav / reload) ──────────────────────────────

  if (!deck || bins.length === 0) {
    return (
      <div className="cat-page cat-page--center">
        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('sort.notFound')}</p>
        <button className="cat-back-btn" onClick={() => navigate('/categories')}>← {t('sort.back')}</button>
      </div>
    );
  }

  // ── Complete screen ───────────────────────────────────────────────────────

  if (phase === 'complete') {
    return (
      <div className="cat-page cat-page--center">
        <div className={celebrating ? 'cat-celebrate' : ''}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
          <h2 style={{ margin: '0 0 8px' }}>{t('sort.wellDone')}</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
            {mistakes === 0
              ? t('sort.noMistakes')
              : `${mistakes} ${t('sort.misses')}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="cat-back-btn" onClick={resetAll}>{t('sort.playAgain')}</button>
          <button className="cat-back-btn cat-back-btn--primary" onClick={() => navigate('/categories')}>
            {t('sort.backToLibrary')}
          </button>
        </div>
      </div>
    );
  }

  // ── Play screen ────────────────────────────────────────────────────────────

  return (
    <div className="cat-page">
      <div className="cat-header">
        <button className="cat-back-btn" onClick={() => navigate('/categories')}>← {t('sort.back')}</button>
        <h1 className="cat-title">{tc(deck.title)}</h1>
        <div className="cat-progress">{sortedCount} / {totalItems}</div>
      </div>

      <p className="cat-instruction">{t('sort.instruction')}</p>

      <div className="cat-bins">
        {bins.map(bin => (
          <div
            key={bin.id}
            className={`cat-bin ${dragOverBin === bin.id ? 'cat-bin--over' : ''}`}
            data-bin={bin.id}
            onDragOver={e => onDragOverBin(e, bin.id)}
            onDrop={e => onDropOnBin(e, bin.id)}
            onDragLeave={() => setDragOverBin(null)}
            style={{ '--bin-color': bin.color || '#EEF2FF' }}
          >
            <div className="cat-bin-header">
              {bin.coverImage && <img src={bin.coverImage} alt="" className="cat-bin-cover" />}
              <span className="cat-bin-label">{tc(bin.label)}</span>
            </div>
            <div className="cat-bin-items">
              {binContents[bin.id]?.map(item => (
                <CatCard key={item.id} item={item} placed />
              ))}
              {(binContents[bin.id]?.length || 0) === 0 && (
                <div className="cat-bin-drop-hint">{t('sort.dropHere')}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <section className="cat-pool-section">
        <div className="cat-pool-hd">
          <span className="cat-pool-label">{t('sort.itemsToSort')}</span>
        </div>
        <div className="cat-pool">
          {pool.map((item, i) => (
            <CatCard
              key={item.id}
              item={item}
              wrong={wrongItemId === item.id}
              dragHandlers={{
                draggable: true,
                onDragStart: (e) => onDragStart(e, i),
                onDragEnd,
                onTouchStart: (e) => onTouchStart(e, i),
              }}
            />
          ))}
        </div>
      </section>
    </div>
  );
};
